/**
 * StewardCommandCenter — Centro de Comando del Soberano (JRS Fase D).
 *
 * Referencia normativa: `JRS_FASE_D_TECHNICAL_PROPOSAL_v1.0.md`.
 *
 * Principios permanentes materializados:
 *   Observación ≠ Decisión ≠ Ejecución ≠ Certificación.
 *
 * Modo inicial: Kiosko. Sólo Zona A (Observación) visible al ingresar.
 * La Zona B (Ejecución) requiere click explícito "Entrar en modo ejecución".
 */

import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API } from '../config';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';

// SOBERANO whitelist (mirror del backend). El backend enforce con 403.
const SOBERANO_EMAILS = new Set([
  'admin@cualapp.com',
  'test_domains@cualapp.com',
]);

const STATE_BADGE = {
  draft: 'bg-slate-200 text-slate-800',
  pending: 'bg-blue-100 text-blue-800',
  analyzing: 'bg-amber-100 text-amber-800',
  proposal: 'bg-violet-100 text-violet-800',
  authorized: 'bg-emerald-100 text-emerald-800',
  executing: 'bg-cyan-100 text-cyan-800',
  validation: 'bg-indigo-100 text-indigo-800',
  completed: 'bg-green-200 text-green-900',
  rejected: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-200 text-gray-700',
};

// Transiciones legales por origen (mirror del backend, para UI dinámica).
const LEGAL_TRANSITIONS = {
  draft: ['pending', 'cancelled'],
  pending: ['analyzing', 'rejected', 'cancelled'],
  analyzing: ['proposal', 'pending', 'rejected'],
  proposal: ['authorized', 'rejected', 'analyzing'],
  authorized: ['executing', 'cancelled'],
  executing: ['validation', 'authorized'],
  validation: ['completed', 'executing'],
  completed: [],
  rejected: [],
  cancelled: [],
};

const AUTH_LEVELS = ['N0', 'N1', 'N2', 'N3'];
const CATEGORIES = [
  'specification', 'development', 'analysis', 'proposal',
  'operational', 'governance', 'review',
];

function StewardCommandCenter({ user, onExit }) {
  const [mode, setMode] = useState('kiosko'); // 'kiosko' | 'execution'
  const [error, setError] = useState(null);
  const [system, setSystem] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [recentActions, setRecentActions] = useState([]);
  const [eedStream, setEedStream] = useState([]);
  const [deliverables, setDeliverables] = useState([]);
  const [snapshots, setSnapshots] = useState([]);
  const [requests, setRequests] = useState([]);
  const [selectedRequestId, setSelectedRequestId] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [refreshTs, setRefreshTs] = useState(0);

  const isSoberano = user?.email && SOBERANO_EMAILS.has(user.email);

  const fetchObservability = useCallback(async () => {
    if (!isSoberano) return;
    setError(null);
    try {
      const [sys, cat, actions, eed, delivs, reqs, snaps] = await Promise.all([
        axios.get(`${API}/steward/observability/system`),
        axios.get(`${API}/steward/jrs/catalog`),
        axios.get(`${API}/steward/observability/recent-actions?limit=20`),
        axios.get(`${API}/steward/observability/eed?limit=50`),
        axios.get(`${API}/steward/observability/deliverables?limit=10`),
        axios.get(`${API}/steward/requests?limit=50`),
        axios.get(`${API}/steward/observability/snapshots?limit=20`),
      ]);
      setSystem(sys.data);
      setCatalog(cat.data.actions);
      setRecentActions(actions.data.items);
      setEedStream(eed.data.items);
      setDeliverables(delivs.data.items);
      setRequests(reqs.data.items);
      setSnapshots(snaps.data.items);
    } catch (e) {
      setError(e.response?.data?.detail?.message || e.message);
    }
  }, [isSoberano]);

  const fetchRequest = useCallback(async (id) => {
    if (!id) { setSelectedRequest(null); return; }
    try {
      const r = await axios.get(`${API}/steward/requests/${id}`);
      setSelectedRequest(r.data);
    } catch (e) {
      setError(e.response?.data?.detail?.message || e.message);
    }
  }, []);

  useEffect(() => { fetchObservability(); }, [fetchObservability, refreshTs]);
  useEffect(() => { fetchRequest(selectedRequestId); }, [selectedRequestId, refreshTs, fetchRequest]);

  const refresh = () => setRefreshTs((t) => t + 1);

  if (!isSoberano) {
    return (
      <div className="p-8 max-w-2xl mx-auto" data-testid="steward-not-soberano">
        <Alert variant="destructive">
          <AlertTitle>Acceso reservado</AlertTitle>
          <AlertDescription>
            El Centro de Comando /admin/steward está reservado al Soberano.
            Sesión actual: <span className="font-mono">{user?.email || 'anónimo'}</span>.
          </AlertDescription>
        </Alert>
        <div className="mt-6">
          <Button variant="outline" onClick={onExit} data-testid="steward-exit-btn">
            Volver al panel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto space-y-6" data-testid="steward-command-center">
      {/* Encabezado */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight" data-testid="steward-title">
            Centro de Comando · Soberano
          </h1>
          <p className="text-sm text-muted-foreground" data-testid="steward-subtitle">
            Observación ≠ Decisión ≠ Ejecución ≠ Certificación
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={refresh} data-testid="steward-refresh-btn">
            Refrescar
          </Button>
          <Button variant="ghost" onClick={onExit} data-testid="steward-exit-btn">
            Salir del panel
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive" data-testid="steward-error-banner">
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{String(error)}</AlertDescription>
        </Alert>
      )}

      {/* ══════ ZONA A · OBSERVACIÓN (siempre visible) ══════ */}
      <section className="space-y-4" data-testid="steward-zone-a">
        <div className="flex items-center gap-3">
          <div className="h-6 w-1 bg-slate-400 rounded" />
          <h2 className="text-lg md:text-xl font-medium">Zona A · Observación (read-only)</h2>
          <Badge variant="outline" data-testid="steward-mode-badge">Modo: {mode}</Badge>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SystemHealthCard system={system} />
          <CatalogViewer catalog={catalog} />
        </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <RecentActionsCard items={recentActions} onSelectRequest={(id) => {
              setMode('execution'); setSelectedRequestId(id);
            }} />
            <DeliverablesCard items={deliverables} onSelectRequest={(id) => {
              setMode('execution'); setSelectedRequestId(id);
            }} />
          </div>
          <EEDStreamCard items={eedStream} />
          <SnapshotsCard items={snapshots} />
        </section>

      {/* Transición entre modos */}
      {mode === 'kiosko' && (
        <div className="border-2 border-dashed border-primary/40 rounded-lg p-6 bg-primary/5" data-testid="steward-enter-execution-panel">
          <p className="text-sm text-muted-foreground mb-3">
            La Zona de Ejecución permite crear y transicionar StewardRequests, ejecutar acciones JRS
            y solicitar diagnóstico. Cada acción queda registrada en EED bajo su identidad soberana.
          </p>
          <Button onClick={() => setMode('execution')} data-testid="steward-enter-execution-btn">
            Entrar en modo ejecución
          </Button>
        </div>
      )}

      {/* ══════ ZONA B · EJECUCIÓN AUTORIZADA ══════ */}
      {mode === 'execution' && (
        <section className="space-y-4 border-2 border-primary/60 rounded-lg p-4 md:p-6 bg-primary/[0.02] shadow-sm" data-testid="steward-zone-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-6 w-1 bg-primary rounded" />
              <h2 className="text-lg md:text-xl font-medium">Zona B · Ejecución Autorizada</h2>
            </div>
            <Button variant="ghost" onClick={() => { setMode('kiosko'); setSelectedRequestId(null); }} data-testid="steward-exit-execution-btn">
              Volver a modo observación
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-1 space-y-4">
              <RequestsList
                items={requests}
                selectedId={selectedRequestId}
                onSelect={setSelectedRequestId}
                onCreated={(newId) => { refresh(); setSelectedRequestId(newId); }}
                onError={setError}
              />
              <RequestDiagnosticCard onCreated={(newId) => {
                refresh(); setSelectedRequestId(newId);
              }} onError={setError} />
            </div>
            <div className="lg:col-span-2">
              {selectedRequest ? (
                <RequestDetail
                  request={selectedRequest}
                  catalog={catalog || []}
                  onChanged={refresh}
                  onError={setError}
                />
              ) : (
                <Card>
                  <CardContent className="py-8 text-center text-muted-foreground text-sm" data-testid="steward-no-request-selected">
                    Seleccione una StewardRequest de la lista o cree una nueva.
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Zona A · sub-componentes de observación
// ---------------------------------------------------------------------------

function SystemHealthCard({ system }) {
  if (!system) {
    return <Card><CardContent className="py-6 text-sm text-muted-foreground">Cargando estado…</CardContent></Card>;
  }
  const sv = system.system_version || {};
  return (
    <Card data-testid="steward-system-health-card">
      <CardHeader><CardTitle className="text-base">Salud del sistema</CardTitle></CardHeader>
      <CardContent className="space-y-1 text-sm">
        <div>Backend uptime: <span className="font-mono">{system.backend_uptime_s ?? '—'}s</span></div>
        <div>Git HEAD: <span className="font-mono text-xs">{sv.git_head ? sv.git_head.slice(0, 12) : 'null'}</span></div>
        <div>Startup: <span className="font-mono text-xs">{sv.startup_ts}</span></div>
        <div>JRS actions ejecutadas total: <span className="font-mono">{system.jrs_action_count}</span></div>
        <div className="mt-2 flex flex-wrap gap-1">
          {Object.entries(system.request_counts_by_state || {})
            .filter(([, n]) => n > 0)
            .map(([st, n]) => (
              <Badge key={st} variant="outline" className={STATE_BADGE[st] || ''}>{st}: {n}</Badge>
            ))}
        </div>
      </CardContent>
    </Card>
  );
}

function CatalogViewer({ catalog }) {
  if (!catalog) return <Card><CardContent className="py-6 text-sm text-muted-foreground">Cargando catálogo…</CardContent></Card>;
  const byMgr = {};
  for (const a of catalog) {
    byMgr[a.manager] = byMgr[a.manager] || [];
    byMgr[a.manager].push(a);
  }
  return (
    <Card data-testid="steward-catalog-viewer">
      <CardHeader><CardTitle className="text-base">Catálogo JRS ({catalog.length} acciones)</CardTitle></CardHeader>
      <CardContent className="space-y-3 text-sm">
        {Object.entries(byMgr).map(([mgr, actions]) => (
          <div key={mgr}>
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">{mgr}</div>
            <div className="flex flex-wrap gap-1">
              {actions.map((a) => (
                <Badge
                  key={a.action_id}
                  variant={a.is_mutation ? 'default' : 'outline'}
                  title={a.description}
                  className="font-mono text-xs"
                  data-testid={`catalog-action-${a.action_id}`}
                >
                  {a.action_id} · {a.required_level}
                </Badge>
              ))}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function RecentActionsCard({ items, onSelectRequest }) {
  return (
    <Card data-testid="steward-recent-actions-card">
      <CardHeader><CardTitle className="text-base">Últimas ejecuciones JRS ({items.length})</CardTitle></CardHeader>
      <CardContent className="space-y-1 text-xs max-h-64 overflow-y-auto">
        {items.length === 0 && <div className="text-muted-foreground">Sin ejecuciones registradas.</div>}
        {items.map((it) => (
          <div key={it.eed_event_id} className="flex items-center justify-between gap-2 py-1 border-b last:border-0">
            <div className="flex-1 min-w-0">
              <div className="font-mono truncate">{it.action_id || it.type}</div>
              <div className="text-muted-foreground truncate">{it.summary || it.reason_code || '—'}</div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-muted-foreground">{it.duration_ms ?? '—'}ms</div>
              {it.correlation_id && (
                <button
                  className="text-primary underline text-xs"
                  onClick={() => onSelectRequest(it.correlation_id)}
                  data-testid={`recent-action-goto-${it.eed_event_id}`}
                >
                  ver request
                </button>
              )}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function DeliverablesCard({ items, onSelectRequest }) {
  return (
    <Card data-testid="steward-deliverables-card">
      <CardHeader><CardTitle className="text-base">Últimos deliverables ({items.length})</CardTitle></CardHeader>
      <CardContent className="space-y-1 text-xs max-h-64 overflow-y-auto">
        {items.length === 0 && <div className="text-muted-foreground">Sin deliverables aún.</div>}
        {items.map((d) => (
          <div key={d.deliverable_id} className="flex items-center justify-between gap-2 py-1 border-b last:border-0">
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{d.title || '(sin título)'}</div>
              <div className="text-muted-foreground truncate font-mono">
                {d.type} · {d.sha256?.slice(0, 12)}… · autor {d.author}
              </div>
            </div>
            <button
              className="text-primary underline text-xs shrink-0"
              onClick={() => onSelectRequest(d.request_id)}
              data-testid={`deliverable-goto-${d.deliverable_id}`}
            >
              ver request
            </button>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function EEDStreamCard({ items }) {
  return (
    <Card data-testid="steward-eed-stream-card">
      <CardHeader><CardTitle className="text-base">Historial EED (últimos {items.length})</CardTitle></CardHeader>
      <CardContent className="text-xs max-h-80 overflow-y-auto">
        {items.length === 0 && <div className="text-muted-foreground">Sin eventos.</div>}
        {items.map((ev) => (
          <div key={ev.id} className="grid grid-cols-12 gap-2 py-1 border-b last:border-0">
            <div className="col-span-3 font-mono text-muted-foreground">{ev.occurred_at?.slice(0, 19)}</div>
            <div className="col-span-4 font-mono">{ev.type}</div>
            <div className="col-span-3 font-mono text-muted-foreground truncate">req={ev.correlation_id?.slice(0, 12)}…</div>
            <div className="col-span-2 truncate">{ev.subject_id}</div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Zona B · sub-componentes de ejecución
// ---------------------------------------------------------------------------

function SnapshotsCard({ items }) {
  const [downloading, setDownloading] = useState(null);
  const download = async (filename) => {
    setDownloading(filename);
    try {
      // Descarga bajo require_soberano; el navegador maneja el streaming.
      const url = `${API}/steward/observability/snapshots/${encodeURIComponent(filename)}`;
      window.open(url, '_blank');
    } finally {
      setDownloading(null);
    }
  };
  return (
    <Card data-testid="steward-snapshots-card">
      <CardHeader>
        <CardTitle className="text-base">Custodia histórica ({items?.length || 0})</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-xs">
        <Alert className="mb-2">
          <AlertTitle className="text-xs">Nota conceptual permanente</AlertTitle>
          <AlertDescription className="text-xs">
            Los bundles listados son <strong>custodias históricas verificables</strong> — no
            son backups operacionales ni certificaciones. Su existencia demuestra que
            una memoria del sistema fue preservada en un momento determinado.
          </AlertDescription>
        </Alert>
        {(!items || items.length === 0) && (
          <div className="text-muted-foreground">
            Sin custodias creadas aún. Para crear una nueva, ejecute la acción
            <code className="mx-1">ops.export_snapshot</code> desde la Zona B con
            una StewardRequest N2 autorizada.
          </div>
        )}
        <div className="max-h-60 overflow-y-auto space-y-1">
          {(items || []).map((s) => (
            <div key={s.filename} className="border-b pb-1 flex items-center justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="font-mono truncate">{s.filename}</div>
                <div className="text-muted-foreground">
                  {s.record_counts?.steward_requests || 0}r / {s.record_counts?.economic_events || 0}e /
                  {' '}{s.record_counts?.deliverables || 0}d · sha256={s.root_sha256?.slice(0, 12)}…
                  · {(s.bytes / 1024).toFixed(1)} KB
                  {s.sanitize_output && <Badge variant="outline" className="ml-1 text-[10px]">sanitized</Badge>}
                </div>
              </div>
              <button
                className="text-primary underline shrink-0 disabled:opacity-50"
                onClick={() => download(s.filename)}
                disabled={downloading === s.filename}
                data-testid={`snapshot-download-${s.filename}`}
              >
                {downloading === s.filename ? 'descargando…' : 'descargar'}
              </button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function RequestsList({ items, selectedId, onSelect, onCreated, onError }) {
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({
    title: '', description: '', category: 'operational', authorization_level: 'N1',
  });
  const [submitting, setSubmitting] = useState(false);

  const create = async () => {
    if (!form.title || !form.description) return;
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/steward/requests`, form);
      setCreateOpen(false);
      setForm({ title: '', description: '', category: 'operational', authorization_level: 'N1' });
      onCreated(r.data.id);
    } catch (e) {
      onError(e.response?.data?.detail?.message || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card data-testid="steward-requests-list">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base">Requests ({items.length})</CardTitle>
        <Button size="sm" onClick={() => setCreateOpen(true)} data-testid="steward-new-request-btn">
          + Nueva
        </Button>
      </CardHeader>
      <CardContent className="space-y-1 text-xs max-h-96 overflow-y-auto">
        {items.map((r) => (
          <button
            key={r.id}
            onClick={() => onSelect(r.id)}
            className={`w-full text-left p-2 rounded border ${selectedId === r.id ? 'border-primary bg-primary/5' : 'border-transparent hover:bg-muted'}`}
            data-testid={`request-item-${r.id}`}
          >
            <div className="font-medium truncate">{r.title}</div>
            <div className="flex items-center gap-1 mt-1">
              <Badge className={STATE_BADGE[r.state] || ''} variant="outline">{r.state}</Badge>
              <span className="text-muted-foreground">·</span>
              <span className="text-muted-foreground">{r.category} · {r.authorization_level}</span>
            </div>
          </button>
        ))}
      </CardContent>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="steward-create-request-dialog">
          <DialogHeader>
            <DialogTitle>Nueva StewardRequest</DialogTitle>
            <DialogDescription>Sólo el Soberano puede crear solicitudes. El estado inicial será `draft`.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              placeholder="Título"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="create-request-title"
            />
            <Textarea
              placeholder="Descripción de la solicitud"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              data-testid="create-request-description"
            />
            <div className="grid grid-cols-2 gap-2">
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="create-request-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={form.authorization_level} onValueChange={(v) => setForm({ ...form, authorization_level: v })}>
                <SelectTrigger data-testid="create-request-level"><SelectValue /></SelectTrigger>
                <SelectContent>{AUTH_LEVELS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancelar</Button>
            <Button onClick={create} disabled={submitting || !form.title || !form.description} data-testid="create-request-submit">
              {submitting ? 'Creando…' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function RequestDetail({ request, catalog, onChanged, onError }) {
  const [busy, setBusy] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [completeText, setCompleteText] = useState('');

  const doTransition = async (to_state) => {
    setBusy(true);
    try {
      await axios.post(`${API}/steward/requests/${request.id}/transition`, {
        to_state, reason: null,
      });
      onChanged();
    } catch (e) {
      onError(e.response?.data?.detail?.message || e.message);
    } finally { setBusy(false); }
  };

  const legalNext = LEGAL_TRANSITIONS[request.state] || [];

  return (
    <Card data-testid={`request-detail-${request.id}`}>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <CardTitle className="text-base truncate">{request.title}</CardTitle>
            <div className="text-xs text-muted-foreground mt-1 font-mono">{request.id}</div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Badge className={STATE_BADGE[request.state]} variant="outline" data-testid="detail-current-state">
              {request.state}
            </Badge>
            <Badge variant="outline">{request.category}</Badge>
            <Badge variant="outline">{request.authorization_level}</Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-sm mb-3 text-muted-foreground whitespace-pre-wrap">{request.description}</p>

        {/* Transición wizard */}
        <div className="mb-4">
          <div className="text-xs uppercase text-muted-foreground mb-1">Transicionar a</div>
          <div className="flex flex-wrap gap-1">
            {legalNext.length === 0 && <span className="text-xs text-muted-foreground">Estado terminal — sin transiciones.</span>}
            {legalNext.map((s) => {
              const isComplete = s === 'completed';
              const variant = isComplete ? 'default' : (['rejected', 'cancelled'].includes(s) ? 'destructive' : 'outline');
              return (
                <Button
                  key={s}
                  variant={variant}
                  size="sm"
                  disabled={busy}
                  onClick={() => isComplete ? setCompleteOpen(true) : doTransition(s)}
                  data-testid={`transition-btn-${s}`}
                >
                  → {s}
                </Button>
              );
            })}
          </div>
        </div>

        <Tabs defaultValue="execute">
          <TabsList data-testid="detail-tabs">
            <TabsTrigger value="execute" data-testid="tab-execute">Ejecutar acción</TabsTrigger>
            <TabsTrigger value="trace" data-testid="tab-trace">Trace EED</TabsTrigger>
            <TabsTrigger value="deliverables" data-testid="tab-deliverables">Deliverables ({request.deliverables?.length || 0})</TabsTrigger>
            <TabsTrigger value="notes" data-testid="tab-notes">Notas</TabsTrigger>
          </TabsList>

          <TabsContent value="execute">
            <ActionExecutor request={request} catalog={catalog} onChanged={onChanged} onError={onError} />
          </TabsContent>
          <TabsContent value="trace">
            <div className="text-xs space-y-1 max-h-72 overflow-y-auto">
              {(request.history || []).map((h) => (
                <div key={h.id} className="border-b pb-1">
                  <span className="font-mono">{h.at?.slice?.(0, 19) || h.at}</span> ·{' '}
                  {h.from_state ? `${h.from_state} → ` : ''}
                  <span className="font-mono">{h.to_state}</span>
                  {' '}<span className="text-muted-foreground">por {h.actor}</span>
                  {h.reason && <div className="text-muted-foreground italic">{h.reason}</div>}
                </div>
              ))}
              <div className="pt-2 text-muted-foreground">
                Eventos EED linkeados: <span className="font-mono">{request.linked_eed_events?.length || 0}</span>
              </div>
            </div>
          </TabsContent>
          <TabsContent value="deliverables">
            <div className="text-xs space-y-1 max-h-72 overflow-y-auto">
              {(request.deliverables || []).length === 0 && (
                <div className="text-muted-foreground">Sin deliverables adjuntos.</div>
              )}
              {(request.deliverables || []).map((d) => (
                <div key={d.id} className="border-b pb-2">
                  <div className="font-medium">{d.title}</div>
                  <div className="text-muted-foreground font-mono">
                    {d.type} · sha256={d.sha256?.slice(0, 12)}… · autor {d.author}
                  </div>
                  <div className="text-muted-foreground font-mono truncate">{d.content_ref}</div>
                  <div className="text-muted-foreground italic mt-1">
                    Para leer el contenido, cree una nueva StewardRequest y ejecute <code>docs.read</code>.
                    Esta consulta quedará registrada en EED.
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
          <TabsContent value="notes">
            <NotesPanel request={request} onChanged={onChanged} onError={onError} />
          </TabsContent>
        </Tabs>
      </CardContent>

      {/* Diálogo de cierre soberano con confirmación textual */}
      <Dialog open={completeOpen} onOpenChange={setCompleteOpen}>
        <DialogContent data-testid="steward-complete-dialog">
          <DialogHeader>
            <DialogTitle>Cerrar StewardRequest como completada</DialogTitle>
            <DialogDescription>
              Este acto es soberano e inmutable en EED. Para confirmar, escriba{' '}
              <code className="font-mono">COMPLETAR {request.id.slice(0, 8)}</code>.
            </DialogDescription>
          </DialogHeader>
          <Input
            placeholder={`COMPLETAR ${request.id.slice(0, 8)}`}
            value={completeText}
            onChange={(e) => setCompleteText(e.target.value)}
            data-testid="complete-confirm-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCompleteOpen(false); setCompleteText(''); }}>Cancelar</Button>
            <Button
              variant="default"
              disabled={completeText !== `COMPLETAR ${request.id.slice(0, 8)}` || busy}
              onClick={async () => {
                await doTransition('completed');
                setCompleteOpen(false);
                setCompleteText('');
              }}
              data-testid="complete-confirm-btn"
            >
              Confirmar cierre soberano
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function ActionExecutor({ request, catalog, onChanged, onError }) {
  const [actionId, setActionId] = useState('');
  const [paramsText, setParamsText] = useState('{}');
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const canExecute = ['authorized', 'executing'].includes(request.state);
  // Filtrar catálogo por nivel de la request (levels comparables por orden).
  const levelOrder = { N0: 0, N1: 1, N2: 2, N3: 3 };
  const reqLevel = levelOrder[request.authorization_level] ?? 0;
  const executableActions = catalog.filter((a) => (levelOrder[a.required_level] ?? 0) <= reqLevel);
  // Directiva soberana Fase D §4: docs.write / docs.append DIFERIDOS del UI.
  const uiActions = executableActions.filter((a) => a.action_id !== 'docs.write' && a.action_id !== 'docs.append');

  const spec = catalog.find((a) => a.action_id === actionId);

  const execute = async () => {
    setBusy(true); setLastResult(null);
    try {
      const parsed = JSON.parse(paramsText || '{}');
      const r = await axios.post(`${API}/steward/requests/${request.id}/execute`, {
        action_id: actionId, params: parsed,
      });
      setLastResult(r.data);
      onChanged();
    } catch (e) {
      onError(e.response?.data?.detail?.message || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="action-executor">
      {!canExecute && (
        <Alert>
          <AlertTitle>Estado no ejecutable</AlertTitle>
          <AlertDescription>
            La request está en <span className="font-mono">{request.state}</span>. Transicione a <span className="font-mono">authorized</span> para ejecutar acciones JRS.
          </AlertDescription>
        </Alert>
      )}
      <Select value={actionId} onValueChange={(v) => { setActionId(v); setParamsText('{}'); }}>
        <SelectTrigger data-testid="executor-action-select"><SelectValue placeholder="Seleccionar acción JRS…" /></SelectTrigger>
        <SelectContent>
          {uiActions.map((a) => (
            <SelectItem key={a.action_id} value={a.action_id} data-testid={`executor-option-${a.action_id}`}>
              {a.action_id === 'ops.export_snapshot'
                ? 'ops.export_snapshot · Crear custodia histórica · N2'
                : `${a.action_id} · ${a.required_level}${a.is_mutation ? ' · mut' : ''}`}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {spec && (
        <div className="text-xs text-muted-foreground">
          {spec.description} · timeout {spec.timeout_s}s
        </div>
      )}
      {actionId === 'ops.export_snapshot' && (
        <Alert>
          <AlertTitle className="text-xs">Crear custodia histórica</AlertTitle>
          <AlertDescription className="text-xs">
            Esta acción genera un bundle firmado con SHA-256 canónico de la memoria del
            ecosistema en el período especificado. No es un backup ni una certificación
            — es evidencia custodiable. Ejemplo params: <code>{'{"period_from":"2026-01-01T00:00:00Z","period_until":"2026-12-31T23:59:59Z"}'}</code>
          </AlertDescription>
        </Alert>
      )}
      <Textarea
        placeholder='Params JSON (ej: {"collection":"steward_requests"})'
        value={paramsText}
        onChange={(e) => setParamsText(e.target.value)}
        className="font-mono text-xs"
        rows={4}
        data-testid="executor-params-textarea"
      />
      <Button onClick={execute} disabled={!canExecute || !actionId || busy} data-testid="executor-run-btn">
        {busy ? 'Ejecutando…' : 'Ejecutar acción'}
      </Button>
      {lastResult && (
        <div className="text-xs bg-muted p-3 rounded" data-testid="executor-result">
          <div className="font-medium mb-1">Resultado</div>
          <div>action: <span className="font-mono">{lastResult.action_id}</span></div>
          <div>executor: <span className="font-mono">{lastResult.executor}</span> · actor: <span className="font-mono">{lastResult.actor}</span></div>
          <div>duración: <span className="font-mono">{lastResult.duration_ms}ms</span></div>
          <div>EED: <span className="font-mono">{lastResult.eed_event_id}</span></div>
          <div>Resumen: {lastResult.summary}</div>
        </div>
      )}
    </div>
  );
}

function NotesPanel({ request, onChanged, onError }) {
  const [content, setContent] = useState('');
  const [busy, setBusy] = useState(false);
  const add = async () => {
    if (!content.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/steward/requests/${request.id}/annotate`, { content });
      setContent(''); onChanged();
    } catch (e) { onError(e.response?.data?.detail?.message || e.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-2 text-xs">
      <div className="max-h-64 overflow-y-auto space-y-2">
        {(request.notes || []).length === 0 && <div className="text-muted-foreground">Sin notas.</div>}
        {(request.notes || []).map((n) => (
          <div key={n.id} className="border-b pb-1">
            <div className="text-muted-foreground font-mono">{n.at?.slice?.(0, 19) || n.at} · {n.author}</div>
            <div className="whitespace-pre-wrap">{n.content}</div>
          </div>
        ))}
      </div>
      <Textarea placeholder="Nueva nota…" value={content} onChange={(e) => setContent(e.target.value)} data-testid="note-content-textarea" />
      <Button size="sm" onClick={add} disabled={busy || !content.trim()} data-testid="note-add-btn">Agregar nota</Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// "Solicitar diagnóstico JRS" (auto-diagnóstico con 4 garantías)
// ---------------------------------------------------------------------------

function RequestDiagnosticCard({ onCreated, onError }) {
  const [check1, setCheck1] = useState(false);
  const [check2, setCheck2] = useState(false);
  const [check3, setCheck3] = useState(false);
  const [busy, setBusy] = useState(false);
  const [steps, setSteps] = useState([]);

  const canStart = check1 && check2 && check3 && !busy;
  const timestamp = new Date().toISOString();

  const addStep = (text, ok) => setSteps((s) => [...s, { text, ok, ts: new Date().toISOString() }]);

  const run = async () => {
    setBusy(true); setSteps([]);
    let reqId = null;
    try {
      addStep('Creando StewardRequest N0 operacional…', null);
      const created = await axios.post(`${API}/steward/requests`, {
        title: `Diagnóstico JRS solicitado ${timestamp}`,
        description: 'Solicitud del Soberano · Javier materializa la ejecución autorizada de tests.jrs_smoke · EED registra el acontecimiento.',
        category: 'operational',
        authorization_level: 'N0',
      });
      reqId = created.data.id;
      setSteps((s) => [{ ...s[0], text: `Creada request ${reqId}`, ok: true }]);

      for (const to of ['pending', 'analyzing', 'proposal', 'authorized']) {
        addStep(`Transición → ${to}…`, null);
        await axios.post(`${API}/steward/requests/${reqId}/transition`, { to_state: to });
        setSteps((s) => s.map((step, i) => i === s.length - 1 ? { ...step, ok: true } : step));
      }

      addStep('Ejecutando tests.jrs_smoke…', null);
      const exec = await axios.post(`${API}/steward/requests/${reqId}/execute`, {
        action_id: 'tests.jrs_smoke',
        params: {},
      });
      const r = exec.data.result || {};
      setSteps((s) => s.map((step, i) => i === s.length - 1 ? {
        ...step,
        text: `Ejecutado tests.jrs_smoke → ${r.passed || 0}p / ${r.failed || 0}f / ${r.skipped || 0}s (${(r.duration_s || 0).toFixed(2)}s)`,
        ok: true,
      } : step));
      addStep('Request queda en authorized. Cierre soberano requiere click aparte.', true);

      onCreated(reqId);
    } catch (e) {
      const msg = e.response?.data?.detail?.message || e.message;
      setSteps((s) => [...s.slice(0, -1), { ...(s[s.length - 1] || {}), text: `Error: ${msg}`, ok: false }]);
      onError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card data-testid="steward-diagnostic-card">
      <CardHeader><CardTitle className="text-base">Solicitar diagnóstico JRS</CardTitle></CardHeader>
      <CardContent className="space-y-3 text-xs">
        <p className="text-muted-foreground">
          Función de conveniencia. Encadena creación → 4 transiciones → ejecución de <code>tests.jrs_smoke</code>.
          Cada paso emite EED. La request quedará en <span className="font-mono">authorized</span>; el cierre
          soberano requiere confirmación aparte.
        </p>
        <label className="flex items-start gap-2">
          <Checkbox checked={check1} onCheckedChange={setCheck1} data-testid="diag-check-1" />
          <span>Confirmo crear una StewardRequest N0 categoría <code>operational</code>.</span>
        </label>
        <label className="flex items-start gap-2">
          <Checkbox checked={check2} onCheckedChange={setCheck2} data-testid="diag-check-2" />
          <span>Confirmo autorizar la ejecución de <code>tests.jrs_smoke</code> bajo mi identidad soberana.</span>
        </label>
        <label className="flex items-start gap-2">
          <Checkbox checked={check3} onCheckedChange={setCheck3} data-testid="diag-check-3" />
          <span>Confirmo que la request quedará en <code>authorized</code> post-ejecución; el cierre a <code>completed</code> requerirá acción aparte.</span>
        </label>
        <Button onClick={run} disabled={!canStart} className="w-full" data-testid="diag-start-btn">
          {busy ? 'Ejecutando pasos…' : 'Iniciar diagnóstico JRS'}
        </Button>
        {steps.length > 0 && (
          <div className="mt-2 space-y-1" data-testid="diag-steps">
            {steps.map((s, i) => (
              <div key={i} className={`text-xs flex items-start gap-2 ${s.ok === false ? 'text-red-600' : ''}`}>
                <span className="font-mono">{s.ok === true ? '✓' : s.ok === false ? '✗' : '…'}</span>
                <span>{s.text}</span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default StewardCommandCenter;
